import React, { useRef, useEffect, useState } from 'react';
import { createCustomEqual, CustomEqualCreatorOptions } from 'fast-equals';
import { isLatLngLiteral } from '@googlemaps/typescript-guards';

import { MapProps, MarkerProps } from './types';
// Icono "navigation-2" de Lucide (https://lucide.dev, licencia ISC),
// vendorizado byte-a-byte en src/assets/icons/nav-arrow-icon.svg (mismo
// polygon data que el original, comment-free; ver ese fichero para el
// `fill="currentColor"` + `stroke="white"` fijo). Import `?raw` de Vite
// (built-in, sin config extra) → string con el markup tal cual. Elegido
// tras feedback usuario 2026-07-07 sobre el cursor GPS anterior (icono de
// coche en un halo, commit 9e24c063): "el rumbo no se lee bien" + "¿por
// qué dentro de una bola blanca?". `navigation-2` es el estándar de
// apps de flota/navegación (flecha de "posición actual + rumbo" — Google
// Maps, Uber) y apunta recto hacia ARRIBA en reposo (polygon "12 2 19 21
// 12 17 5 21", ápice en x=12,y=2), así que `rotate(bearingDeg)` con la
// convención de bearing (0=norte, sentido horario) mapea DIRECTO sobre el
// icono sin ningún offset de corrección.
import navArrowIconSvg from '../../../assets/icons/nav-arrow-icon.svg?raw';
// Glifo "canvi de sentit" (↺, dibujo propio inline, sin vendorizar de
// ninguna librería externa) — usado por el marcador de cambio de sentido de
// MapWithSegments (SR), ver `createDirectionChangeMarkerContent`.
import directionChangeIconSvg from '../../../assets/icons/direction-change-icon.svg?raw';

// ═══════════════════════════════════════════════════════════════════
// Internal shared primitives para MapComponent y MultiPathMap.
//
// PR #70 (main 2026-07-02): migración de `google.maps.Marker` (deprecated
// en junio 2024) a `google.maps.marker.AdvancedMarkerElement`. Mismo
// approach que `Map/index.tsx` en main. Los consumidores de este archivo
// (`MultiPathMap` del proyecto y `MapWithSegments` del SR P6) heredan el
// fix sin cambios adicionales.
//
// `AdvancedMarkerElement` requiere:
//   1. La librería `'marker'` cargada en el useLoadScript (ya la incluye
//      MapComponent/MultiPathMap tras el merge de PR #70).
//   2. Un `mapId` fijado en la construcción del mapa (no vía setOptions).
// ═══════════════════════════════════════════════════════════════════

const MAP_ID = import.meta.env.VITE_MAP_ID || 'DEMO_MAP_ID';

// Padding (px) usado al encuadrar el viewport a las coordenadas de
// `fitBoundsCoordinates` — ver InternalMapProps.
const FIT_BOUNDS_PADDING_PX = 40;

export interface InternalMapProps extends MapProps {
  /**
   * Cuando se proporcionan >=2 coordenadas, el mapa ignora `center`/`zoom`
   * y encuadra el viewport a estos puntos (`fitBounds` + padding). Único
   * consumidor: MapWithSegments (SR) — pasa SOLO las coordenadas de los
   * tramos (nunca la traza GPS), así los outliers GPS no expanden el
   * encuadre. Si se omite u tiene <2 puntos, se mantiene el comportamiento
   * previo (center/zoom explícitos vía `options`).
   */
  fitBoundsCoordinates?: google.maps.LatLngLiteral[];
  /**
   * Botón de recentrar (2026-07-08): entra en la clave de igualdad del
   * bail de fitBounds (`lastFitKeyRef`) — incrementarlo re-encuadra aunque
   * las coordenadas sean idénticas a las del último fit (re-click del
   * botón sobre el mismo tramo/recorrido). Los re-renders pasivos (mismo
   * nonce + mismas coordenadas) siguen sin re-encuadrar: el bail sigue
   * protegiendo el pan/zoom manual del usuario.
   */
  fitBoundsNonce?: number;
  /** Padding (px) del fitBounds — default `FIT_BOUNDS_PADDING_PX` (40). */
  fitBoundsPadding?: number;
}

export const Map: React.FC<InternalMapProps> = ({
  onClick,
  onIdle,
  children,
  style,
  fitBoundsCoordinates,
  fitBoundsNonce,
  fitBoundsPadding,
  ...options
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  // Firma del último fitBounds aplicado — bail de igualdad: re-renders con
  // NUEVA identidad de array pero mismas coordenadas (polling del padre,
  // remounts) no re-encuadran en bucle ni pelean con el pan/zoom manual.
  const lastFitKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (ref.current && !map) {
      // mapId solo puede fijarse en la construcción; no via setOptions.
      setMap(new window.google.maps.Map(ref.current, { mapId: MAP_ID }));
    }
  }, [ref, map]);

  useDeepCompareEffectForMaps(() => {
    if (map) {
      map.setOptions(options);
    }
  }, [map, options]);

  // Encuadre a los tramos — declarado DESPUÉS del setOptions de arriba para
  // que, en el mismo commit, gane sobre el center/zoom fijo cuando aplique.
  // El acceso a `window.google` va guardado explícitamente (defensa en
  // profundidad: este efecto solo corre con `map` ya construido — post-carga
  // del script — pero ningún acceso al global queda sin guard).
  useEffect(() => {
    if (!map || !fitBoundsCoordinates || fitBoundsCoordinates.length < 2) {
      return;
    }
    if (typeof window.google === 'undefined' || !window.google.maps) {
      return;
    }
    // El nonce prefija la clave: mismas coordenadas + nonce distinto =
    // re-encuadre forzado (botón de recentrar); ver InternalMapProps.
    const fitKey = `${fitBoundsNonce ?? 0}|${fitBoundsCoordinates
      .map((c) => `${c.lat},${c.lng}`)
      .join(';')}`;
    if (fitKey === lastFitKeyRef.current) return;
    lastFitKeyRef.current = fitKey;
    const bounds = new window.google.maps.LatLngBounds();
    fitBoundsCoordinates.forEach((coord) => bounds.extend(coord));
    map.fitBounds(bounds, fitBoundsPadding ?? FIT_BOUNDS_PADDING_PX);
  }, [map, fitBoundsCoordinates, fitBoundsNonce, fitBoundsPadding]);

  useEffect(() => {
    if (map) {
      ['click', 'idle'].forEach((eventName) =>
        google.maps.event.clearListeners(map, eventName)
      );

      if (onClick) {
        map.addListener('click', onClick);
      }

      if (onIdle) {
        map.addListener('idle', () => onIdle(map));
      }
    }
  }, [map, onClick, onIdle]);

  return (
    <>
      <div ref={ref} style={style} />
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          // set the map prop on the child component
          // @ts-ignore
          return React.cloneElement(child, { map });
        }
      })}
    </>
  );
};

export interface InternalPathProps extends google.maps.PolylineOptions {
  /**
   * Click/tap handler sobre la polyline (2026-07-08, selección de tramo
   * desde el propio trazado en MapWithSegments/SR). Se registra vía
   * `polyline.addListener('click', ...)` y el listener se limpia en el
   * cleanup del efecto (unmount o cambio de identidad del handler) — mismo
   * patrón que el listener de click de `Marker` más abajo. Requiere
   * `clickable: true` en las options (decisión del consumidor; sin
   * `onClick`, las polylines de este proyecto van con `clickable: false`).
   */
  onClick?: () => void;
}

export const Path: React.FC<InternalPathProps> = ({ onClick, ...options }) => {
  const [path, setPath] = useState<google.maps.Polyline>();

  useEffect(() => {
    if (!path) {
      setPath(new google.maps.Polyline());
    }

    return () => {
      if (path) {
        path.setMap(null);
      }
    };
  }, [path]);

  useEffect(() => {
    if (path) {
      path.setOptions(options);
    }
  }, [path, options]);

  // Click — reatacha si `onClick` cambia de identidad; el cleanup del
  // efecto elimina SIEMPRE el listener anterior (también en unmount), así
  // que nunca se acumulan listeners huérfanos sobre la Polyline.
  useEffect(() => {
    if (!path || !onClick) return;
    const listener = path.addListener('click', () => onClick());
    return () => listener.remove();
  }, [path, onClick]);

  return null;
};

// Círculo blanco de 20px centrado (réplica del antiguo icon Symbol);
// `translateY(50%)` compensa el anclaje bottom-center de AdvancedMarkerElement.
// Mismo helper que `Map/index.tsx` de main.
const createDefaultMarkerContent = () => {
  const content = document.createElement('div');
  content.style.width = '20px';
  content.style.height = '20px';
  content.style.borderRadius = '50%';
  content.style.backgroundColor = 'white';
  content.style.border = '1px solid black';
  content.style.transform = 'translateY(50%)';
  return content;
};

/**
 * Convierte un `google.maps.Symbol` (fillColor / strokeColor / strokeWeight)
 * en un `HTMLElement` compatible con `AdvancedMarkerElement`. Los consumidores
 * del SR (`MultiPathMap`, `MapWithSegments`) definen marcadores como círculos
 * coloreados por estado — la conversión preserva ese look sin rewrite.
 */
const createMarkerContentFromSymbol = (symbol: google.maps.Symbol) => {
  const content = document.createElement('div');
  content.setAttribute('data-role', 'dot');
  content.style.width = '20px';
  content.style.height = '20px';
  content.style.borderRadius = '50%';
  content.style.backgroundColor = String(symbol.fillColor ?? 'white');
  const strokeWeight = symbol.strokeWeight ?? 1;
  content.style.border = `${strokeWeight}px solid ${String(symbol.strokeColor ?? 'black')}`;
  content.style.opacity = String(symbol.fillOpacity ?? 1);
  content.style.transform = 'translateY(50%)';
  return content;
};

// Tamaño de la flecha de rumbo (cursor GPS con bearing conocido) y del
// punto de respaldo (cursor GPS sin bearing — 1 solo punto GPS, o
// últimos 2 puntos idénticos). Flecha en el rango pedido 26-28px; punto
// de respaldo a la escala del marker circular por defecto (20px, ver
// `createDefaultMarkerContent`) para que no desentone al promocionarse.
const CURSOR_ARROW_SIZE_PX = 28;
const CURSOR_DOT_SIZE_PX = 20;

// Área táctil de los marcadores pulsables (2026-08-10). El punto MIDE 22 px,
// por debajo del mínimo AA de WCAG 2.2 (24×24 px CSS) y a la mitad de lo que
// recomiendan Apple (44 pt) y Material (48 dp). En una tablet en carretera,
// con guantes, eso es un objetivo muy pequeño — y ahora los puntos hacen más
// cosas (abrir el abanico, encuadrar el grupo, seleccionar el tramo).
//
// Se agranda el ÁREA sin agrandar el DIBUJO: el círculo visible se envuelve en
// una caja transparente de 44 px. La compensación de anclaje (`translateY(50%)`,
// que centra el contenido sobre la coordenada porque `AdvancedMarkerElement`
// ancla abajo-centro) pasa a la caja, así que el punto sigue cayendo
// exactamente donde caía.
const TOUCH_TARGET_SIZE_PX = 44;

const wrapWithTouchTarget = (visual: HTMLDivElement): HTMLDivElement => {
  const wrapper = document.createElement('div');
  wrapper.setAttribute('data-role', 'marker-touch-target');
  wrapper.style.width = `${TOUCH_TARGET_SIZE_PX}px`;
  wrapper.style.height = `${TOUCH_TARGET_SIZE_PX}px`;
  wrapper.style.display = 'flex';
  wrapper.style.alignItems = 'center';
  wrapper.style.justifyContent = 'center';
  wrapper.style.transform = 'translateY(50%)';
  // El dibujo ya no compensa el anclaje: lo hace la caja.
  visual.style.transform = '';
  wrapper.appendChild(visual);
  return wrapper;
};

// Marcador "cambio de sentido" de MapWithSegments (SR) — mismo tamaño de
// punto que el marcador de pausa (20px) + un poco de aire extra para que el
// glifo interior (↺) no quede pegado al borde.
const DIRECTION_CHANGE_MARKER_SIZE_PX = 22;
const DIRECTION_CHANGE_GLYPH_SIZE_PX = 13;

/**
 * Marcador de "cambio de sentido" (MapWithSegments/SR) — círculo relleno con
 * el color pasado (por diseño, un token DISTINTO de los colores D7 de causa
 * de pausa — ver comentario en MapWithSegments sobre por qué `garnet` no
 * sirve: coincide con `primary`/basic en ambos temas) + anillo blanco (igual
 * contraste que `createMarkerContentFromSymbol`) + glifo ↺ inline
 * (`directionChangeIconSvg`, dibujo propio) centrado, coloreado con
 * `strokeColor` vía `currentColor` (mismo mecanismo que
 * `createCursorRotationLayer`).
 */
const createDirectionChangeMarkerContent = (
  fillColor: string,
  strokeColor: string,
  strokeWeight: number
): HTMLDivElement => {
  const content = document.createElement('div');
  content.setAttribute('data-role', 'dot');
  content.style.width = `${DIRECTION_CHANGE_MARKER_SIZE_PX}px`;
  content.style.height = `${DIRECTION_CHANGE_MARKER_SIZE_PX}px`;
  content.style.borderRadius = '50%';
  content.style.backgroundColor = fillColor;
  content.style.border = `${strokeWeight}px solid ${strokeColor}`;
  content.style.boxShadow = '0 1px 4px rgba(0, 0, 0, 0.35)';
  content.style.display = 'flex';
  content.style.alignItems = 'center';
  content.style.justifyContent = 'center';
  content.style.color = strokeColor;
  content.style.transform = 'translateY(50%)';
  const glyph = document.createElement('div');
  glyph.style.width = `${DIRECTION_CHANGE_GLYPH_SIZE_PX}px`;
  glyph.style.height = `${DIRECTION_CHANGE_GLYPH_SIZE_PX}px`;
  glyph.innerHTML = directionChangeIconSvg;
  content.appendChild(glyph);
  return content;
};

// Contador de convergencia (2026-08-10): tamaño de la chapita con el número
// de tramos que terminan en el MISMO punto. Ver `createConvergenceDotContent`.
const DOT_BADGE_SIZE_PX = 14;
const DOT_BADGE_FONT_PX = 9;
// Grosor del anillo cuando el dot representa al tramo SELECCIONADO — mismo
// papel que la `casingColor` negra de la polyline seleccionada.
const DOT_SELECTED_STROKE_PX = 3;

/**
 * Marcador de un punto donde CONVERGEN varios tramos (o donde uno solo está
 * seleccionado dentro de un grupo).
 *
 * Un recorrido de vigilancia va y vuelve por la misma vía, así que varios
 * tramos terminan en el MISMO PK — y por tanto en la misma coordenada exacta.
 * Antes se dibujaba un marcador por tramo y se repartían "en corona" con un
 * radio fijo de ~3 m, que a la escala de un recorrido entero es 0,06 px: se
 * tapaban entre ellos y el mapa enseñaba menos paradas de las que hubo (y
 * escondía cambios de sentido). Ahora hay UN dot por coordenada:
 *
 *   - `neutralFill` → el grupo mezcla causas distintas y ninguna se puede
 *     afirmar (más aún cuando agrupa por PROXIMIDAD: son paradas distintas en
 *     sitios distintos). Relleno neutro; las causas se leen al acercar.
 *   - `fillColor` solo → el dot representa a UN tramo concreto (el único del
 *     grupo, o el seleccionado), con SU color real.
 *   - `badgeCount` → chapita con el número de tramos que convergen.
 *   - `selected` → anillo `accentColor` grueso, espejo de la casing de la
 *     polyline seleccionada.
 */
const createConvergenceDotContent = (o: {
  fillColor: string;
  neutralFill?: boolean;
  strokeColor: string;
  strokeWeight: number;
  withGlyph: boolean;
  badgeCount?: number;
  selected?: boolean;
  accentColor: string;
}): HTMLDivElement => {
  const content = document.createElement('div');
  content.setAttribute('data-role', 'dot');
  content.style.position = 'relative';
  content.style.width = `${DIRECTION_CHANGE_MARKER_SIZE_PX}px`;
  content.style.height = `${DIRECTION_CHANGE_MARKER_SIZE_PX}px`;
  content.style.borderRadius = '50%';
  // Un grupo con VARIAS causas no puede afirmar un color: son paradas
  // distintas, en sitios distintos, agrupadas solo porque a este zoom caen
  // juntas. Se pinta neutro y las causas se leen al acercar. Con una sola
  // causa (o un tramo suelto) sí se afirma su color.
  content.style.backgroundColor = o.neutralFill
    ? o.accentColor
    : o.fillColor;
  if (o.neutralFill) content.dataset.neutralFill = 'true';
  content.style.border = o.selected
    ? `${DOT_SELECTED_STROKE_PX}px solid ${o.accentColor}`
    : `${o.strokeWeight}px solid ${o.strokeColor}`;
  content.style.boxShadow = '0 1px 4px rgba(0, 0, 0, 0.35)';
  content.style.display = 'flex';
  content.style.alignItems = 'center';
  content.style.justifyContent = 'center';
  content.style.color = o.strokeColor;
  content.style.transform = 'translateY(50%)';

  if (o.withGlyph) {
    const glyph = document.createElement('div');
    glyph.style.width = `${DIRECTION_CHANGE_GLYPH_SIZE_PX}px`;
    glyph.style.height = `${DIRECTION_CHANGE_GLYPH_SIZE_PX}px`;
    glyph.innerHTML = directionChangeIconSvg;
    content.appendChild(glyph);
  }

  if (o.badgeCount && o.badgeCount > 1) {
    const badge = document.createElement('div');
    badge.setAttribute('data-role', 'convergence-badge');
    badge.style.position = 'absolute';
    badge.style.top = `-${Math.round(DOT_BADGE_SIZE_PX / 3)}px`;
    badge.style.right = `-${Math.round(DOT_BADGE_SIZE_PX / 3)}px`;
    badge.style.width = `${DOT_BADGE_SIZE_PX}px`;
    badge.style.height = `${DOT_BADGE_SIZE_PX}px`;
    badge.style.borderRadius = '50%';
    badge.style.backgroundColor = o.strokeColor;
    badge.style.border = `1px solid ${o.accentColor}`;
    badge.style.color = o.accentColor;
    badge.style.fontSize = `${DOT_BADGE_FONT_PX}px`;
    badge.style.fontWeight = '600';
    badge.style.lineHeight = `${DOT_BADGE_SIZE_PX - 2}px`;
    badge.style.textAlign = 'center';
    badge.textContent = String(o.badgeCount);
    content.appendChild(badge);
  }

  return content;
};

/**
 * Firma del content de un dot (marcador que NO es el cursor GPS): todo lo que
 * decide cómo se dibuja. El efecto de re-pintado de `Marker` la compara para
 * saber si hay que reconstruir el `content` del `AdvancedMarkerElement` ya
 * montado — sin esto, un dot creado sin glifo ↺ (o con otra causa de pausa)
 * se quedaba congelado con su dibujo inicial de por vida.
 */
const dotContentSignature = (
  symbol: google.maps.Symbol,
  withGlyph: boolean,
  extra?: {
    neutralFill?: boolean;
    badgeCount?: number;
    selected?: boolean;
    accentColor?: string;
  }
): string =>
  [
    withGlyph ? 'glyph' : 'plain',
    String(symbol.fillColor ?? ''),
    String(symbol.strokeColor ?? ''),
    String(symbol.strokeWeight ?? ''),
    String(symbol.fillOpacity ?? ''),
    extra?.neutralFill ? 'neutral' : '',
    String(extra?.badgeCount ?? ''),
    extra?.selected ? 'sel' : '',
    String(extra?.accentColor ?? ''),
  ].join('|');

/** Opciones de dibujo de un dot que no dependen del `google.maps.Symbol`. */
interface DotExtras {
  neutralFill?: boolean;
  badgeCount?: number;
  selected?: boolean;
  accentColor?: string;
}

/**
 * Punto ÚNICO de decisión de qué dibujo lleva un dot. Lo usan tanto el efecto
 * de creación como el de re-pintado del `Marker`, para que no puedan divergir
 * (que un dot se creara de una forma y se repintara de otra fue justo la
 * clase de bug del glifo congelado).
 */
const buildDotContent = (
  symbolIcon: google.maps.Symbol,
  withGlyph: boolean,
  extras: DotExtras
): HTMLDivElement => {
  const isConvergence =
    Boolean(extras.neutralFill) ||
    (extras.badgeCount ?? 0) > 1 ||
    Boolean(extras.selected);
  if (isConvergence) {
    return wrapWithTouchTarget(
      createConvergenceDotContent({
      fillColor: String(symbolIcon.fillColor ?? 'black'),
      neutralFill: extras.neutralFill,
      strokeColor: String(symbolIcon.strokeColor ?? 'white'),
      strokeWeight: symbolIcon.strokeWeight ?? 2,
      withGlyph,
      badgeCount: extras.badgeCount,
        selected: extras.selected,
        accentColor: extras.accentColor ?? 'black',
      })
    );
  }
  return wrapWithTouchTarget(
    withGlyph
      ? createDirectionChangeMarkerContent(
          String(symbolIcon.fillColor ?? 'black'),
          String(symbolIcon.strokeColor ?? 'white'),
          symbolIcon.strokeWeight ?? 2
        )
      : createMarkerContentFromSymbol(symbolIcon)
  );
};

const CURSOR_ROTATION_ATTR = 'data-role';
const CURSOR_ROTATION_VALUE = 'cursor-rotation-layer';

/**
 * Capa que aplica SOLO la rotación (bearing) al icono de flecha — envuelve
 * el SVG inyectado (`navArrowIconSvg`) en un elemento propio con
 * `transform-origin: center`, aislado del wrapper exterior (que hace la
 * compensación de anclaje `translateY(50%)`, ver `createCursorArrowContent`)
 * para que rotar nunca desplace el punto de anclaje real sobre la
 * coordenada GPS.
 */
const createCursorRotationLayer = (
  color: string,
  rotationDeg: number
): HTMLDivElement => {
  const layer = document.createElement('div');
  layer.setAttribute(CURSOR_ROTATION_ATTR, CURSOR_ROTATION_VALUE);
  layer.style.position = 'absolute';
  layer.style.inset = '0';
  layer.style.transformOrigin = 'center';
  layer.style.transform = `rotate(${rotationDeg}deg)`;
  layer.style.pointerEvents = 'none';
  layer.style.color = color; // cascada `currentColor` → colorea navArrowIconSvg
  layer.innerHTML = navArrowIconSvg;
  return layer;
};

/**
 * Cursor GPS con rumbo conocido (MapWithSegments/SR) — feedback usuario
 * 2026-07-07 round 3 sobre el cursor-coche+halo del commit 9e24c063: "el
 * rumbo no se lee bien" + "¿por qué dentro de una bola blanca?". Estándar
 * de apps de flota/navegación (Google Maps, Uber, trackers) para "posición
 * actual + rumbo": una flecha de navegación que SÍ rota entera al bearing
 * — nada de icono fijo + indicador separado. `navArrowIconSvg` apunta
 * recto hacia arriba en reposo, así que `rotate(bearingDeg)` (bearing
 * 0=norte, sentido horario) mapea directo sin offset. Relleno = color
 * propio (garnet — DISTINTO del color de las líneas de traza GPS,
 * `routeStatus.active`) + contorno blanco fijo (vendorizado en el SVG)
 * para contraste sobre cualquier fondo de mapa. Sin halo.
 */
const createCursorArrowContent = (
  color: string,
  rotationDeg: number
): HTMLDivElement => {
  const content = document.createElement('div');
  content.style.width = `${CURSOR_ARROW_SIZE_PX}px`;
  content.style.height = `${CURSOR_ARROW_SIZE_PX}px`;
  content.style.position = 'relative';
  content.style.transform = 'translateY(50%)';
  content.appendChild(createCursorRotationLayer(color, rotationDeg));
  return content;
};

/**
 * Cursor GPS sin rumbo conocido todavía (1 solo punto GPS, o últimos 2
 * puntos idénticos): punto relleno simple, mismo color que la flecha +
 * contorno blanco, SIN flecha — no hay sentido de marcha real que
 * mostrar. Se promociona a `createCursorArrowContent` en cuanto llega el
 * primer bearing (ver efecto de rotación de `Marker` más abajo).
 */
const createCursorDotContent = (color: string): HTMLDivElement => {
  const content = document.createElement('div');
  content.style.width = `${CURSOR_DOT_SIZE_PX}px`;
  content.style.height = `${CURSOR_DOT_SIZE_PX}px`;
  content.style.borderRadius = '50%';
  content.style.backgroundColor = color;
  content.style.border = '2px solid white';
  content.style.boxShadow = '0 1px 4px rgba(0, 0, 0, 0.35)';
  content.style.transform = 'translateY(50%)';
  return content;
};

export interface InternalMarkerProps extends MarkerProps {
  /**
   * Rumbo (grados, 0 = norte, sentido horario) calculado desde los 2
   * últimos puntos GPS. Cuando se conoce, y el marker es `cursorIcon`, la
   * flecha de rumbo rota a este ángulo (ver `createCursorArrowContent`).
   * Único consumidor: cursor GPS de MapWithSegments (SR).
   */
  rotationDeg?: number;
  /**
   * Cuando es `true` junto con `symbolIcon`, el marker se renderiza como
   * cursor GPS (flecha de rumbo si `rotationDeg` está definido, punto de
   * respaldo si no) en vez del círculo coloreado por defecto. Discriminador
   * EXPLÍCITO — NO se infiere de `rotationDeg`: con un único punto GPS el
   * rumbo es `undefined` pero el marker debe seguir siendo el cursor
   * (como punto, no como el círculo genérico por defecto).
   */
  cursorIcon?: boolean;
  /**
   * Cuando es `true` junto con `symbolIcon`, el marker se renderiza como el
   * glifo "cambio de sentido" (círculo + ↺ inline, ver
   * `createDirectionChangeMarkerContent`) en vez del círculo coloreado por
   * defecto. Discriminador explícito, mismo patrón que `cursorIcon`. Único
   * consumidor: MapWithSegments (SR).
   */
  directionChangeIcon?: boolean;
  /**
   * El grupo reúne tramos con causas DISTINTAS: no hay un color que se pueda
   * afirmar, así que el dot se pinta con `accentColor` (neutro) y las causas
   * se leen al acercar. Único consumidor: MapWithSegments (SR).
   */
  neutralFill?: boolean;
  /** Nº de tramos que convergen en el punto; >1 pinta la chapita contador. */
  badgeCount?: number;
  /**
   * El dot representa al tramo SELECCIONADO (dentro de un grupo que converge):
   * anillo `accentColor` grueso, espejo de la casing de la polyline elegida.
   */
  selectedOutline?: boolean;
  /** Color del anillo de selección y del contador (negro en el consumidor). */
  accentColor?: string;
}

export const Marker: React.FC<InternalMarkerProps> = ({
  position,
  map,
  symbolIcon,
  rotationDeg,
  cursorIcon,
  directionChangeIcon,
  neutralFill,
  badgeCount,
  selectedOutline,
  accentColor,
  onClick,
}) => {
  const [marker, setMarker] =
    useState<google.maps.marker.AdvancedMarkerElement>();
  // Recuerda si el content montado es el cursor GPS (vs. círculo por
  // defecto) — decidido UNA vez al crear el marker, no cambia con el
  // rumbo.
  const isCursorRef = useRef(false);
  // Último rumbo aplicado — bail de igualdad del efecto de rotación.
  const lastRotationRef = useRef<number | null>(null);
  // Firma del content montado para los dots (todo lo que NO es cursor) — bail
  // de igualdad del efecto de re-pintado, mismo patrón que `lastRotationRef`.
  const lastDotSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (!marker) {
      const isCursor = symbolIcon !== undefined && cursorIcon === true;
      isCursorRef.current = isCursor;
      const color = String(
        symbolIcon?.strokeColor ?? symbolIcon?.fillColor ?? 'black'
      );
      let content: HTMLElement;
      if (isCursor && rotationDeg !== undefined) {
        lastRotationRef.current = rotationDeg;
        content = createCursorArrowContent(color, rotationDeg);
      } else if (isCursor) {
        content = createCursorDotContent(color);
      } else if (symbolIcon) {
        const withGlyph = directionChangeIcon === true;
        const extras = {
          neutralFill,
          badgeCount,
          selected: selectedOutline,
          accentColor,
        };
        lastDotSignatureRef.current = dotContentSignature(
          symbolIcon,
          withGlyph,
          extras
        );
        content = buildDotContent(symbolIcon, withGlyph, extras);
      } else {
        content = createDefaultMarkerContent();
      }
      // `gmpClickable` por defecto es `false` — sin esto un marker con
      // `onClick` nunca dispara el evento 'click' (ver
      // AdvancedMarkerElementOptions.gmpClickable en @types/google.maps).
      const options: google.maps.marker.AdvancedMarkerElementOptions = {
        content,
      };
      if (onClick) {
        options.gmpClickable = true;
      }
      setMarker(new google.maps.marker.AdvancedMarkerElement(options));
    }

    return () => {
      if (marker) {
        marker.map = null;
      }
    };
    // symbolIcon/cursorIcon/directionChangeIcon/rotationDeg solo se leen
    // para decidir el content INICIAL; las actualizaciones posteriores las
    // gestionan los dos efectos de abajo (rotación del cursor y re-pintado
    // del dot), que mutan `marker.content` in-place sin recrear el
    // AdvancedMarkerElement.
  }, [marker]);

  // Click — reatacha el listener si `onClick` cambia de identidad (el
  // `content`/`AdvancedMarkerElement` NO se recrea, solo el listener).
  // 2026-07-20: `addListener('click')` está deprecado en
  // AdvancedMarkerElement (warning "<gmp-advanced-marker>: Please use
  // addEventListener('gmp-click', ...)") — es un HTMLElement, así que se
  // usa el evento DOM `gmp-click`. `gmpClickable` se marca para que Maps
  // trate el marker como interactivo (cursor pointer / hit-target).
  useEffect(() => {
    if (!marker || !onClick) return;
    const handler = () => onClick();
    (marker as { gmpClickable?: boolean }).gmpClickable = true;
    marker.addEventListener('gmp-click', handler);
    return () => marker.removeEventListener('gmp-click', handler);
  }, [marker, onClick]);

  // Cursor GPS (SR): reorienta la flecha en vivo según llegan nuevos
  // puntos GPS. Si el content montado es la flecha, muta SOLO el
  // `transform` de la capa de rotación — el resto del content no se
  // toca. Si el content montado es el punto de respaldo (bearing inicial
  // `undefined`) y ahora llega el primer bearing conocido, se PROMOCIONA
  // reemplazando `marker.content` por la flecha completa — ocurre como
  // mucho una vez, al segundo punto GPS distinto del primero. Si el
  // rumbo pasa a `undefined` de nuevo (2 últimos puntos idénticos), el
  // efecto no hace nada: se conserva la última flecha conocida en vez de
  // retirarla. Bail de igualdad por ref: si el efecto re-corre por
  // identidad con el MISMO rumbo, no toca el DOM.
  useEffect(() => {
    if (!marker || !isCursorRef.current || rotationDeg === undefined) return;
    if (lastRotationRef.current === rotationDeg) return;
    lastRotationRef.current = rotationDeg;
    if (!(marker.content instanceof HTMLElement)) return;
    const color = String(
      symbolIcon?.strokeColor ?? symbolIcon?.fillColor ?? 'black'
    );
    const existingLayer = marker.content.querySelector<HTMLElement>(
      `[${CURSOR_ROTATION_ATTR}="${CURSOR_ROTATION_VALUE}"]`
    );
    if (existingLayer) {
      existingLayer.style.transform = `rotate(${rotationDeg}deg)`;
      return;
    }
    marker.content = createCursorArrowContent(color, rotationDeg);
  }, [marker, rotationDeg, symbolIcon]);

  // Dots (todo lo que NO es el cursor GPS): re-pinta el content cuando cambia
  // lo que lo define — el glifo ↺ de cambio de sentido o los colores de la
  // causa de pausa.
  //
  // Bug 2026-08-10: el content se decidía SOLO al montar, y los consumidores
  // dan a cada marcador una `key` estable (`pause-<idTramo>` en
  // `MultiPathMap`), así que el componente no se remonta mientras el mapa
  // siga vivo — y el detalle del RV tampoco lo remonta al revalidar (su
  // loader resuelve los datos antes de `()`, así que el `<Await>` no
  // suspende). Un dot montado sin glifo se quedaba sin él para siempre: es
  // lo que pasaba con poca cobertura, cuando el tramo del giro se queda en la
  // cola offline y solo aparece al drenarla. Mismo patrón que el efecto de
  // rotación de arriba: mutar `marker.content`, nunca recrear el marcador
  // (recrearlo perdería el listener de click y haría parpadear el mapa).
  useEffect(() => {
    if (!marker || isCursorRef.current || !symbolIcon) return;
    const withGlyph = directionChangeIcon === true;
    const extras = {
      neutralFill,
      badgeCount,
      selected: selectedOutline,
      accentColor,
    };
    const signature = dotContentSignature(symbolIcon, withGlyph, extras);
    if (lastDotSignatureRef.current === signature) return;
    lastDotSignatureRef.current = signature;
    marker.content = buildDotContent(symbolIcon, withGlyph, extras);
  }, [
    marker,
    directionChangeIcon,
    symbolIcon,
    neutralFill,
    badgeCount,
    selectedOutline,
    accentColor,
  ]);

  useEffect(() => {
    if (marker) {
      marker.position = position;
      marker.map = map ?? null;
    }
  }, [marker, position, map]);

  return null;
};

const deepCompareEqualsForMaps = createCustomEqual(((
    deepEqual: <A, B>(a: A, b: B) => boolean
  ) =>
  (a: google.maps.LatLng, b: google.maps.LatLng) => {
    if (
      isLatLngLiteral(a) ||
      a instanceof google.maps.LatLng ||
      isLatLngLiteral(b) ||
      b instanceof google.maps.LatLng
    ) {
      return new google.maps.LatLng(a).equals(new google.maps.LatLng(b));
    }
    return deepEqual(a, b);
  }) as CustomEqualCreatorOptions<undefined>);

function useDeepCompareMemoize(value: object | undefined) {
  const ref = React.useRef<object>();
  if (!deepCompareEqualsForMaps(value, ref.current)) {
    ref.current = value;
  }
  return ref.current;
}

export function useDeepCompareEffectForMaps(
  callback: React.EffectCallback,
  dependencies: [google.maps.Map | undefined, google.maps.MapOptions]
) {
  React.useEffect(callback, dependencies.map(useDeepCompareMemoize));
}
